import File from "../../structures/file";
import Directory from "../../structures/directory";
import App from "../../structures/app";
import { UUID } from "../../utils.js";
import { deleteDriveFile } from "../../api/gapi";

export const TYPES = {
  FILE: 0,
  DIR: 1,
  APP: 2,
};

export const defaultRawData = {
  dirs: {
    root: {
      name: "root",
      hash: "root",
      parent: null,
      children: {},
      type: TYPES.DIR,
    },
    bin: {
      name: "bin",
      hash: "bin",
      parent: null,
      children: {},
      type: TYPES.DIR,
    },
  },
  files: {},
  apps: {},
};

class FileSystem {
  /**
   * create a filesystem from its raw data
   * @param {Object} rawData
   * @param {Array} saveCallBacks
   * @param {Function} reportError
   */
  constructor(rawData, saveCallBacks, reportError = console.log) {
    if (!rawData) {
      rawData = defaultRawData;
    }
    const dirData = rawData.dirs;
    const fileData = rawData.files;
    const appData = rawData.apps;
    const dirNodes = {};
    const fileNodes = {};
    const appNodes = {};

    for (let key in dirData) {
      const dir = dirData[key];
      dirNodes[key] = new Directory(
        dir.name,
        dir.hash,
        dir.parent,
        dir.children
      );
    }
    for (let key in fileData) {
      const file = fileData[key];
      fileNodes[key] = new File(
        file.name,
        file.hash,
        file.parent,
        file.data,
        file.ext,
        file.extra ? file.extra : {}
      );
    }
    for (let key in appData) {
      const app = appData[key];
      appNodes[key] = new App(
        app.name,
        app.hash,
        app.parent,
        app.data,
        app.icon,
        app.appData
      );
    }

    this.dirNodes = dirNodes;
    this.fileNodes = fileNodes;
    this.appNodes = appNodes;
    this.saveCallBacks = saveCallBacks;
    this.saveData = () => {
      for (let func of saveCallBacks) {
        func();
      }
    };
    this.onRemove = () => {};
    this.onInstall = () => {};
    this.reportError = reportError;
  }

  /**
   * get the raw data from a filesystem
   * @returns {Object}
   */
  outputRawData() {
    const dirData = {};
    const fileData = {};
    const appData = {};
    const { dirNodes, fileNodes, appNodes } = this;
    for (let key in dirNodes) {
      const dir = dirNodes[key];
      dirData[key] = {
        name: dir.getName(),
        hash: dir.getHash(),
        parent: dir.getParent(),
        children: dir.getChildren(),
        type: dir.getType(),
      };
    }
    for (let key in fileNodes) {
      const file = fileNodes[key];
      fileData[key] = {
        name: file.getName(),
        hash: file.getHash(),
        parent: file.getParent(),
        data: file.getData(),
        type: file.getType(),
        ext: file.getExt(),
        extra: file.getExtra(),
      };
    }
    for (let key in appNodes) {
      const app = appNodes[key];
      appData[key] = {
        name: app.getName(),
        hash: app.getHash(),
        parent: app.getParent(),
        data: app.getData(),
        type: app.getType(),
        icon: app.getIcon(),
        appData: app.getAppData(),
      };
    }
    return {
      dirs: dirData,
      files: fileData,
      apps: appData,
    };
  }

  /**
   * get the size of filesystem's raw data
   * @returns {Number}
   */
  getRawSize() {
    return JSON.stringify(this.outputRawData()).length;
  }

  /**
   * get the number of directories in the filesystem
   * @returns {Number}
   */
  getNumDirs() {
    return Object.keys(this.dirNodes).length;
  }

  /**
   * get the number of files in the filesystem
   * @returns {Number}
   */
  getNumFiles() {
    return Object.keys(this.fileNodes).length;
  }

  /**
   * get the number of apps in the filesystem
   * @returns {Number}
   */
  getNumApps() {
    return Object.keys(this.appNodes).length;
  }

  /**
   * determine if the target metadata indicates it's of File type
   * @param {Object} meta
   * @returns {Boolean}
   */
  isFile(meta) {
    return meta.type === TYPES.FILE;
  }

  /**
   * determine if the target metadata indicates it's of Directory type
   * @param {Object} meta
   * @returns {Boolean}
   */
  isDir(meta) {
    return meta.type === TYPES.DIR;
  }

  /**
   * determine if the target metadata indicates it's of App type
   * @param {Object} meta
   * @returns {Boolean}
   */
  isApp(meta) {
    return meta.type === TYPES.APP;
  }

  /**
   * Add a new callback function for saving the filesystem
   * @param {Function} func
   */
  addSaveCallBack(func) {
    this.saveCallBacks.push(func);
  }

  /**
   * set the callback function for reporting errors
   * @param {Function} func
   */
  changeReportError(func) {
    this.reportError = func;
  }

  /**
   * set the callback function for removal of any node
   * @param {Function} func
   */
  setOnRemove(func) {
    this.onRemove = func;
  }

  /**
   * UNSAFE: reset the file system to its default value
   */
  _UNSAFE_RESET() {
    this.dirNodes = {
      root: new Directory("root", "root", null, {}),
      bin: new Directory("bin", "bin", null, {}),
    };
    this.fileNodes = {};
    this.appNodes = {};
    this.saveData();
  }

  /**
   * get file from its hash
   * @param {String} hash
   * @returns {File}
   */
  getFile(hash) {
    return this.fileNodes[hash];
  }

  /**
   * get directory from its hash
   * @param {String} hash
   * @returns {Directory}
   */
  getDirectory(hash) {
    return this.dirNodes[hash];
  }

  /**
   * get App from its hash
   * @param {String} hash
   * @returns {App}
   */
  getApp(hash) {
    return this.appNodes[hash];
  }

  /**
   * get the child to a parent with its given name
   * @param {String} hash
   * @param {String} name
   * @returns {File|Directory}
   */
  getChildByName(hash, name) {
    let children = this.retrieveDirectoryContent(hash);
    if (!children) {
      return null;
    }
    for (let child of Object.values(children)) {
      if (child.name === name) {
        if (this.isDir(child)) {
          return this.dirNodes[child.hash];
        } else if (this.isFile(child)) {
          return this.fileNodes[child.hash];
        } else {
          this.reportError("Filesystem corrupted!");
        }
      }
    }
    return null;
  }

  /**
   * get current directory from its path
   * @param {Array} path
   * @returns {Directory}
   */
  getDirByPath(path) {
    if (path.shift() !== "root") {
      this.reportError("Path not start with root");
      return null;
    }
    let currentDirHash = "root";
    while (path.length > 0) {
      const currentDirName = path.shift();
      const child = this.getChildByName(currentDirHash, currentDirName);
      if (!child) {
        this.reportError(`'${currentDirName}' not found`);
        return null;
      }
      if (this.isDir(child.getMetaData())) {
        currentDirHash = child.getHash();
      } else {
        this.reportError(`'${currentDirName}' is not a directory`);
        return null;
      }
    }
    return this.dirNodes[currentDirHash];
  }

  getNodeByPath(path) {
    if (path.shift() !== "root") {
      this.reportError("Path not start with root");
      return null;
    }
    let currentHash = "root";
    while (path.length > 0) {
      const currentDirName = path.shift();
      const child = this.getChildByName(currentHash, currentDirName);
      if (!child) {
        this.reportError(`'${currentDirName}' not found`);
        return null;
      }
      if (this.isDir(child.getMetaData())) {
        currentHash = child.getHash();
      } else if (this.isFile(child.getMetaData())) {
        currentHash = child.getHash();
        if (path.length > 0) {
          this.reportError(`'${currentDirName}' has no children`);
          return null;
        }
        return this.fileNodes[currentHash];
      } else {
        this.reportError(`'${currentDirName}' is not a valid node`);
        return null;
      }
    }
    return this.dirNodes[currentHash];
  }

  /**
   * get the path to a directory
   * @param {String} hash
   * @returns {Array}
   */
  findDirPath(hash) {
    const path = [];
    let node = this.dirNodes[hash];
    while (node.getParent() !== null) {
      path.push(node.getName());
      node = this.dirNodes[node.getParent()];
    }
    path.push(node.getName());
    path = path.reverse();
    return path;
  }

  // check name consistency

  checkName(parent, name, selfHash = null) {
    const siblings = this.dirNodes[parent].getChildren();
    for (let hash in siblings) {
      if (hash === selfHash) {
        continue;
      }
      const childName = siblings[hash].name;
      if (name === childName) {
        this.reportError("Name '" + name + "' has already been taken!");
        return false;
      }
    }
    return true;
  }

  checkDirectoryName(name) {
    if (name === "") {
      this.reportError("Directory name '" + name + "' cannot be empty!");
      return false;
    } else if (name.length >= 25) {
      this.reportError(
        "Directory name '" + name + "' exceeds the length limit!"
      );
      return false;
    } else {
      return true;
    }
  }

  checkFileName(name) {
    if (!name) {
      this.reportError("File name '" + name + "' cannot be empty!");
      return false;
    } else if (name.length >= 25) {
      this.reportError("File name '" + name + "' exceeds the length limit!");
      return false;
    } else {
      return true;
    }
  }

  // create and remove

  createNewDirectory(parent, name) {
    if (this.checkName(parent, name) && this.checkDirectoryName(name)) {
      const newHash = UUID();
      const newDir = new Directory(name, newHash, parent, {});
      this.dirNodes[newHash] = newDir;
      this.dirNodes[parent].addChild(newHash, newDir.getMetaData());
      this.saveData();
      return newHash;
    } else {
      return null;
    }
  }

  createNewFile(parent, name, data, ext = null, extra = {}) {
    if (data === null || data === undefined) {
      this.reportError("The file data is corrupted");
      return null;
    }
    if (this.checkName(parent, name) && this.checkFileName(name)) {
      const newHash = UUID();
      const newFile = new File(name, newHash, parent, data, ext, extra);
      this.fileNodes[newHash] = newFile;
      this.dirNodes[parent].addChild(newHash, newFile.getMetaData());
      this.saveData();
      return newHash;
    } else {
      this.reportError("The file name '" + name + "' is already taken");
      return null;
    }
  }

  installNewApp(name, data, icon) {
    const newHash = UUID();
    const newApp = new App(name, newHash, null, data, icon);
    this.appNodes[newHash] = newApp;
    this.saveData();
    return newHash;
  }

  removeNode(parent, hash) {
    const removed = this.moveNode(parent, hash, "bin");
    this.onRemove();
    return removed;
  }

  uninstallApp(hash) {
    delete this.appNodes[hash];
    this.saveData();
  }

  // clear trash bin

  deepRemoveNode(hash) {
    const metadata = this.dirNodes["bin"].getChild(hash);
    if (metadata.type === TYPES.DIR) {
      this.deepRemoveDirectory(hash);
    } else if (metadata.type === TYPES.FILE) {
      this.deepRemoveFile(hash);
    }
  }

  deepRemoveFile(hash) {
    const file = this.fileNodes[hash];
    const parentNode = this.getDirectory(file.getParent());
    parentNode.removeChild(hash);
    if (file.getExtra().onDrive) {
      this.cleanUpDriveFile(file);
    }
    delete this.fileNodes[hash];
    this.saveData();
  }

  cleanUpDriveFile = async (file) => {
    await deleteDriveFile(file.getExtra().fileId);
  };

  deepRemoveDirectory(hash) {
    const dir = this.dirNodes[hash];
    const childNodes = dir.getChildren();
    const parentNode = this.getDirectory(dir.getParent());
    for (let key in childNodes) {
      const child = childNodes[key];
      if (child.type === TYPES.DIR) {
        this.deepRemoveDirectory(child.hash);
      } else if (child.type === TYPES.FILE) {
        this.deepRemoveFile(child.hash);
      }
    }
    parentNode.removeChild(hash);
    delete this.dirNodes[hash];
    this.saveData();
  }

  // list

  retrieveDirectoryContent(hash) {
    const dir = this.dirNodes[hash];
    if (!dir) {
      this.reportError(`'${hash}' is not a directory hash`);
      return null;
    }
    const childNodes = dir.getChildren();
    return childNodes;
  }

  retrieveApps() {
    return this.appNodes;
  }

  getDirectorySize(hash) {
    const childNodes = this.retrieveDirectoryContent(hash);
    let size = 0;
    for (let key in childNodes) {
      if (childNodes[key].type === TYPES.FILE) {
        size += childNodes[key].size;
      } else {
        size += this.getDirectorySize(childNodes[key].hash);
      }
    }
    return size;
  }

  // move

  moveNode(parent, hash, destination) {
    if (parent !== hash && destination !== hash) {
      const metadata = this.dirNodes[parent].getChild(hash);
      if (metadata.type === TYPES.DIR) {
        const child = this.dirNodes[hash];
        child.changeParent(destination);
        this.dirNodes[parent].removeChild(hash);
        this.dirNodes[destination].addChild(hash, child.getMetaData());
      } else {
        const child = this.fileNodes[hash];
        child.changeParent(destination);
        this.dirNodes[parent].removeChild(hash);
        this.dirNodes[destination].addChild(hash, child.getMetaData());
      }
      this.saveData();
      return true;
    } else {
      this.reportError("Cannot move a directory into itself");
      return false;
    }
  }

  // install

  installApp(hash, icon) {
    const file = this.fileNodes[hash];
    const app = new App(file.getName(), hash, null, file.getData(), icon);
    this.appNodes[hash] = app;
    this.saveData();
    this.onInstall();
  }

  // app storage

  getAppStorage(hash, key) {
    const app = this.appNodes[hash];
    const value = app.getStorage(key);
    return value;
  }

  setAppStorage(hash, key, value) {
    const app = this.appNodes[hash];
    app.setStorage(key, value);
    this.saveData();
  }

  // save and modify

  changeFileName(hash, name) {
    const file = this.fileNodes[hash];
    const parent = file.getParent();
    if (this.checkName(parent, name, hash) && this.checkFileName(name)) {
      file.changeName(name);
      const parent = this.dirNodes[file.getParent()];
      parent.addChild(hash, file.getMetaData());
      this.saveData();
      return true;
    } else {
      return false;
    }
  }

  changeDirectoryName(hash, name) {
    const dir = this.dirNodes[hash];
    const parent = dir.getParent();
    if (this.checkName(parent, name, hash) && this.checkDirectoryName(name)) {
      dir.changeName(name);
      const parent = this.dirNodes[dir.getParent()];
      parent.addChild(hash, dir.getMetaData());
      this.saveData();
      return true;
    } else {
      return false;
    }
  }

  saveFile(hash, data) {
    this.fileNodes[hash].saveData(data);
    this.saveData();
  }
}

export default FileSystem;
